import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Home, Building2, ChevronRight, Plus, Trash2, MapPin, Hash, FileText, Upload, Loader2 } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const AddCompany = () => {
  const navigate = useNavigate();
  const { id: companyId } = useParams();
  const isEditMode = Boolean(companyId);
  const { toast } = useToast();

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const BUSINESS_MODE_OPTIONS = ["LOGISTICS", "FREIGHT_FORWARDING"];
  const SHIPMENT_TYPE_OPTIONS = ["ROAD", "RAIL", "AIR", "SEA", "COURIER"];
  const MONTH_OPTIONS = [
    { value: 1, label: "January" },
    { value: 2, label: "February" },
    { value: 3, label: "March" },
    { value: 4, label: "April" },
    { value: 5, label: "May" },
    { value: 6, label: "June" },
    { value: 7, label: "July" },
    { value: 8, label: "August" },
    { value: 9, label: "September" },
    { value: 10, label: "October" },
    { value: 11, label: "November" },
    { value: 12, label: "December" }
  ];

  const createBranchState = (branch = {}) => ({
    id: branch.id || `branch-${Date.now()}-${Math.random().toString(16).slice(2)}`,
    backendId: branch.backendId || null,
    nickName: branch.nickName || '',
    addressLine1: branch.addressLine1 || '',
    addressLine2: branch.addressLine2 || '',
    state: branch.state || '',
    city: branch.city || '',
    country: branch.country || 'India',
    pincode: branch.pincode || '',
    gstNumber: branch.gstNumber || '',
    contactPerson: branch.contactPerson || '',
    designation: branch.designation || '',
    contactNumber: branch.contactNumber || '',
    email: branch.email || '',
    isHeadOffice: branch.isHeadOffice || false
  });

  const [companyData, setCompanyData] = useState({
    name: '',
    code: '',
    email: '',
    phone: '',
    secondaryPhone: '',
    address: '',
    logoUrl: '',
    gstNumber: '',
    panNumber: '',
    cinNumber: '',
    businessModes: [],
    shipmentTypes: [],
    defaultMode: 'LOGISTICS',
    status: 'active'
  });
  
  const [logoFile, setLogoFile] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  // Company-level docket series (ONE per company)
  const [docketSeries, setDocketSeries] = useState({
    docket_prefix: '',
    next_number: 1,
    padding_digits: 6,
    reset_rule: 'FY',
    fy_start_month: 4,
    allow_manual_printed_docket_no: true
  });

  // Company-level estimate series (ONE per company)
  const [estimateSeries, setEstimateSeries] = useState({
    estimate_prefix: '',
    next_number: 1,
    padding_digits: 4,
  });

  const [branches, setBranches] = useState(() => [createBranchState({ isHeadOffice: true })]);
  const [removedBranchIds, setRemovedBranchIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [availableSchemas, setAvailableSchemas] = useState([]);
  const [schemaMappings, setSchemaMappings] = useState({});

  const getAuthHeaders = useCallback(() => {
    if (!backendUrl) {
      throw new Error('Backend URL is not configured.');
    }

    const token = localStorage.getItem('auth_token');
    if (!token) {
      throw new Error('Please log in as a Super Admin to manage companies.');
    }

    return {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  }, [backendUrl]);

  const getAuthHeadersFormData = useCallback(() => {
    const token = localStorage.getItem('auth_token');
    if (!token) {
      throw new Error('Please log in to continue.');
    }

    return {
      Authorization: `Bearer ${token}`
      // Don't set Content-Type for FormData - browser sets it automatically with boundary
    };
  }, []);

  const getErrorMessage = async (response, fallbackMessage) => {
    try {
      const data = await response.json();
      return data?.detail || fallbackMessage;
    } catch (error) {
      return fallbackMessage;
    }
  };

  const mapCompanyToForm = (company) => ({
    name: company?.name || '',
    code: company?.code || '',
    email: company?.email || '',
    phone: company?.phone || '',
    secondaryPhone: company?.phone_secondary || '',
    address: company?.address || '',
    logoUrl: company?.logo_url || '',
    gstNumber: company?.gst_number || '',
    panNumber: company?.pan_number || '',
    cinNumber: company?.cin_number || '',
    businessModes: company?.business_modes || [],
    shipmentTypes: company?.allowed_shipment_types || [],
    defaultMode: company?.default_mode || 'LOGISTICS',
    status: typeof company?.status === 'string' ? company.status.toLowerCase() : 'active'
  });

  const mapBranchFromApi = (branch) =>
    createBranchState({
      id: branch.id,
      backendId: branch.id,
      nickName: branch.nick_name || '',
      addressLine1: branch.address_line1 || '',
      addressLine2: branch.address_line2 || '',
      state: branch.state || '',
      city: branch.city || '',
      country: branch.country || 'India',
      pincode: branch.pincode || '',
      gstNumber: branch.gst_number || '',
      contactPerson: branch.contact_person || '',
      designation: branch.designation || '',
      contactNumber: branch.contact_number || '',
      email: branch.email || '',
      isHeadOffice: branch.is_head_office || false
    });

  useEffect(() => {
    const loadFormSchemas = async () => {
      try {
        const headers = getAuthHeaders();
        const response = await fetch(`${backendUrl}/api/form-schemas?is_active=true`, { headers });
        if (response.ok) {
          const data = await response.json();
          setAvailableSchemas(data);
        }
      } catch (error) {
        console.error('Error loading form schemas:', error);
      }
    };

    const seedAndLoadSchemas = async () => {
      try {
        const headers = getAuthHeaders();
        await fetch(`${backendUrl}/api/form-schemas/seed`, { method: 'POST', headers });
      } catch (e) { /* ignore */ }
      await loadFormSchemas();
    };

    seedAndLoadSchemas();
  }, [backendUrl, getAuthHeaders]);

  useEffect(() => {
    const loadCompanyDetails = async () => {
      if (!isEditMode) {
        return;
      }

      try {
        setLoading(true);
        setErrorMessage('');
        setSuccessMessage('');

        const headers = getAuthHeaders();
        const [companyResponse, branchesResponse, schemaMappingsResponse] = await Promise.all([
          fetch(`${backendUrl}/api/companies/${companyId}`, { headers }),
          fetch(`${backendUrl}/api/branches?company_id=${companyId}`, { headers }),
          fetch(`${backendUrl}/api/companies/${companyId}/schema-mappings`, { headers })
        ]);

        if (!companyResponse.ok) {
          const message = await getErrorMessage(companyResponse, 'Failed to load company details.');
          throw new Error(message);
        }

        const company = await companyResponse.json();
        const branchesData = branchesResponse.ok ? await branchesResponse.json() : [];

        setCompanyData(mapCompanyToForm(company));

        // Load docket series from company response
        if (company.docket_series) {
          setDocketSeries({
            docket_prefix: company.docket_series.docket_prefix || '',
            next_number: company.docket_series.next_number || 1,
            padding_digits: company.docket_series.padding_digits || 6,
            reset_rule: company.docket_series.reset_rule || 'FY',
            fy_start_month: company.docket_series.fy_start_month || 4,
            allow_manual_printed_docket_no: company.docket_series.allow_manual_printed_docket_no ?? true
          });
        }

        // Load estimate series from company response
        if (company.estimate_series) {
          setEstimateSeries({
            estimate_prefix: company.estimate_series.estimate_prefix || '',
            next_number: company.estimate_series.next_number || 1,
            padding_digits: company.estimate_series.padding_digits || 4,
          });
        }

        // Load schema mappings
        if (schemaMappingsResponse.ok) {
          const mappingsData = await schemaMappingsResponse.json();
          const mappingsMap = {};
          mappingsData.forEach(m => {
            mappingsMap[m.shipment_type] = m.schema_key;
          });
          setSchemaMappings(mappingsMap);
        }

        if (Array.isArray(branchesData) && branchesData.length > 0) {
          const mappedBranches = branchesData.map(mapBranchFromApi);
          if (!mappedBranches.some((branch) => branch.isHeadOffice)) {
            mappedBranches[0].isHeadOffice = true;
          }
          setBranches(mappedBranches);
        } else {
          setBranches([createBranchState({ isHeadOffice: true })]);
        }
        setRemovedBranchIds([]);
      } catch (error) {
        setErrorMessage(error.message || 'Failed to load company details.');
      } finally {
        setLoading(false);
      }
    };

    loadCompanyDetails();
  }, [companyId, isEditMode, backendUrl, getAuthHeaders]);

  const handleCompanyChange = (field, value) => {
    setCompanyData(prev => ({ ...prev, [field]: value }));
  };


  const handleLogoUpload = async () => {
    if (!logoFile) {
      toast({
        title: "Error",
        description: "Please select a logo file",
        variant: "destructive"
      });
      return;
    }

    try {
      setUploadingLogo(true);
      const formData = new FormData();
      formData.append('file', logoFile);

      const response = await fetch(
        `${backendUrl}/api/drive/upload?module=company_logos`,
        {
          method: 'POST',
          headers: getAuthHeadersFormData(),
          body: formData
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to upload logo');
      }

      const data = await response.json();
      
      // Use Google Drive thumbnail URL which works better for embedding
      let logoUrl = '';
      if (data.file_id) {
        // Use thumbnail format which is more reliable for img src
        logoUrl = `https://drive.google.com/thumbnail?id=${data.file_id}&sz=w400`;
      } else if (data.web_content_link) {
        logoUrl = data.web_content_link;
      } else if (data.web_view_link) {
        // Extract ID from web_view_link and use thumbnail format
        const match = data.web_view_link.match(/\/d\/([^\/]+)/);
        if (match) {
          logoUrl = `https://drive.google.com/thumbnail?id=${match[1]}&sz=w400`;
        } else {
          logoUrl = data.web_view_link;
        }
      }
      
      setCompanyData(prev => ({ ...prev, logoUrl }));
      setLogoFile(null);
      
      toast({
        title: "Success",
        description: "Logo uploaded successfully"
      });
    } catch (error) {
      console.error('Error uploading logo:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to upload logo",
        variant: "destructive"
      });
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleRemoveLogo = () => {
    setCompanyData(prev => ({ ...prev, logoUrl: '' }));
    setLogoFile(null);
  };


  const toggleCompanyArrayValue = (field, value) => {
    setCompanyData(prev => {
      const currentValues = prev[field] || [];
      const isRemoving = currentValues.includes(value);
      const updatedValues = isRemoving
        ? currentValues.filter(item => item !== value)
        : [...currentValues, value];
      
      return { ...prev, [field]: updatedValues };
    });
  };

  const handleDocketSeriesChange = (field, value) => {
    setDocketSeries(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleEstimateSeriesChange = (field, value) => {
    setEstimateSeries(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSchemaMappingChange = (shipmentType, schemaKey) => {
    setSchemaMappings(prev => {
      const next = { ...prev };
      if (schemaKey) {
        next[shipmentType] = schemaKey;
      } else {
        delete next[shipmentType];
      }
      return next;
    });
  };

  const handleBranchChange = (branchId, field, value) => {
    setBranches(prev => prev.map(branch => 
      branch.id === branchId ? { ...branch, [field]: value } : branch
    ));
  };

  const addNewBranch = () => {
    const newBranch = createBranchState({ isHeadOffice: false });
    setBranches(prev => [...prev, newBranch]);
  };

  const removeBranch = (branchId) => {
    if (branches.length > 1) {
      const branchToRemove = branches.find(branch => branch.id === branchId);
      if (branchToRemove?.backendId) {
        setRemovedBranchIds(prev => [...prev, branchToRemove.backendId]);
      }
      setBranches(prev => prev.filter(branch => branch.id !== branchId));
    }
  };

  const validateForm = () => {
    if (!companyData.name || !companyData.code || !companyData.email || !companyData.phone) {
      setErrorMessage('Please fill in all required company fields.');
      return false;
    }

    // Validate docket series if prefix is provided
    if (docketSeries.docket_prefix && !docketSeries.docket_prefix.trim()) {
      setErrorMessage('Please enter a valid Docket Prefix.');
      return false;
    }

    const incompleteBranch = branches.find(
      branch => !branch.nickName || !branch.addressLine1 || !branch.state || !branch.city || !branch.pincode || !branch.contactPerson || !branch.designation || !branch.contactNumber
    );

    if (incompleteBranch) {
      setErrorMessage('Please complete all required branch fields before saving.');
      return false;
    }

    return true;
  };

  const buildBranchPayload = (branch) => ({
    nick_name: branch.nickName,
    address_line1: branch.addressLine1,
    address_line2: branch.addressLine2,
    state: branch.state,
    city: branch.city,
    country: branch.country,
    pincode: branch.pincode,
    gst_number: branch.gstNumber,
    contact_person: branch.contactPerson,
    designation: branch.designation,
    contact_number: branch.contactNumber,
    email: branch.email,
    is_head_office: branch.isHeadOffice
  });

  const saveDocketSeries = async (targetCompanyId, headers) => {
    // Only save if prefix is provided
    if (!docketSeries.docket_prefix || !docketSeries.docket_prefix.trim()) {
      return;
    }

    const response = await fetch(`${backendUrl}/api/companies/${targetCompanyId}/docket-series`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        docket_prefix: docketSeries.docket_prefix.toUpperCase(),
        next_number: docketSeries.next_number || 1,
        padding_digits: docketSeries.padding_digits || 6,
        reset_rule: docketSeries.reset_rule || 'FY',
        fy_start_month: docketSeries.fy_start_month || 4,
        allow_manual_printed_docket_no: docketSeries.allow_manual_printed_docket_no ?? true
      })
    });

    if (!response.ok) {
      const message = await getErrorMessage(response, 'Failed to save docket series.');
      throw new Error(message);
    }
  };

  const saveEstimateSeries = async (targetCompanyId, headers) => {
    if (!estimateSeries.estimate_prefix || !estimateSeries.estimate_prefix.trim()) {
      return;
    }
    const response = await fetch(`${backendUrl}/api/companies/${targetCompanyId}/estimate-series`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        estimate_prefix: estimateSeries.estimate_prefix.toUpperCase(),
        next_number: estimateSeries.next_number || 1,
        padding_digits: estimateSeries.padding_digits || 4,
      })
    });
    if (!response.ok) {
      const message = await getErrorMessage(response, 'Failed to save estimate series.');
      throw new Error(message);
    }
  };

  const saveSchemaMappings = async (targetCompanyId, headers) => {
    const mappingsToSave = Object.entries(schemaMappings)
      .filter(([_, schemaKey]) => schemaKey)
      .map(([shipmentType, schemaKey]) => ({
        shipment_type: shipmentType,
        schema_key: schemaKey,
        schema_version: 1,
        is_active: true
      }));

    const response = await fetch(`${backendUrl}/api/companies/${targetCompanyId}/schema-mappings/bulk`, {
      method: 'POST',
      headers,
      body: JSON.stringify(mappingsToSave)
    });

    if (!response.ok) {
      const message = await getErrorMessage(response, 'Failed to save schema mappings.');
      throw new Error(message);
    }
  };

  const handleSave = async () => {
    try {
      setErrorMessage('');
      setSuccessMessage('');

      if (!validateForm()) {
        return;
      }

      setSaving(true);

      const headers = getAuthHeaders();
      if (isEditMode) {
        const updatePayload = {
          name: companyData.name,
          email: companyData.email,
          phone: companyData.phone,
          phone_secondary: companyData.secondaryPhone || null,
          address: companyData.address,
          logo_url: companyData.logoUrl || null,
          gst_number: companyData.gstNumber || null,
          pan_number: companyData.panNumber || null,
          cin_number: companyData.cinNumber || null,
          business_modes: companyData.businessModes,
          allowed_shipment_types: companyData.shipmentTypes,
          default_mode: companyData.defaultMode,
          status: companyData.status
        };

        const companyResponse = await fetch(`${backendUrl}/api/companies/${companyId}`, {
          method: 'PUT',
          headers,
          body: JSON.stringify(updatePayload)
        });

        if (!companyResponse.ok) {
          const message = await getErrorMessage(companyResponse, 'Failed to update company.');
          throw new Error(message);
        }

        // Save docket series
        await saveDocketSeries(companyId, headers);
        await saveEstimateSeries(companyId, headers);

        // Save schema mappings
        await saveSchemaMappings(companyId, headers);

        const branchRequests = [];

        branches.forEach((branch) => {
          const branchPayload = buildBranchPayload(branch);
          if (branch.backendId) {
            branchRequests.push(
              fetch(`${backendUrl}/api/branches/${branch.backendId}`, {
                method: 'PUT',
                headers,
                body: JSON.stringify(branchPayload)
              })
            );
          } else {
            branchRequests.push(
              fetch(`${backendUrl}/api/branches`, {
                method: 'POST',
                headers,
                body: JSON.stringify({
                  company_id: companyId,
                  ...branchPayload
                })
              })
            );
          }
        });

        removedBranchIds.forEach((branchId) => {
          branchRequests.push(
            fetch(`${backendUrl}/api/branches/${branchId}`, {
              method: 'DELETE',
              headers
            })
          );
        });

        if (branchRequests.length > 0) {
          const responses = await Promise.all(branchRequests);
          const failedResponse = responses.find(response => !response.ok);
          if (failedResponse) {
            const message = await getErrorMessage(failedResponse, 'Failed to save branch updates.');
            throw new Error(message);
          }
        }

        setSuccessMessage('Company updated successfully.');
      } else {
        const createPayload = {
          name: companyData.name,
          code: companyData.code,
          email: companyData.email,
          phone: companyData.phone,
          phone_secondary: companyData.secondaryPhone || null,
          address: companyData.address,
          logo_url: companyData.logoUrl || null,
          gst_number: companyData.gstNumber || null,
          pan_number: companyData.panNumber || null,
          cin_number: companyData.cinNumber || null,
          business_modes: companyData.businessModes,
          allowed_shipment_types: companyData.shipmentTypes,
          default_mode: companyData.defaultMode,
          status: companyData.status,
          branches: branches.map(buildBranchPayload)
        };

        const companyResponse = await fetch(`${backendUrl}/api/companies-with-branches`, {
          method: 'POST',
          headers,
          body: JSON.stringify(createPayload)
        });

        if (!companyResponse.ok) {
          const message = await getErrorMessage(companyResponse, 'Failed to create company.');
          throw new Error(message);
        }

        const newCompany = await companyResponse.json();
        
        // Save docket series for new company
        await saveDocketSeries(newCompany.id, headers);
        await saveEstimateSeries(newCompany.id, headers);

        // Save schema mappings for new company
        await saveSchemaMappings(newCompany.id, headers);

        setSuccessMessage('Company created successfully.');
      }

      setTimeout(() => navigate('/settings/companies'), 800);
    } catch (error) {
      setErrorMessage(error.message || 'Failed to save company.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm font-ibm">
        <Home className="w-4 h-4 text-text-secondary" />
        <span className="text-text-secondary">Home</span>
        <ChevronRight className="w-4 h-4 text-text-secondary" />
        <Building2 className="w-4 h-4 text-text-secondary" />
        <span className="text-text-secondary">Settings</span>
        <ChevronRight className="w-4 h-4 text-text-secondary" />
        <span
          data-testid="company-breadcrumb-label"
          className="text-accent-blue font-medium"
        >
          {isEditMode ? 'Edit Company' : 'Add Company'}
        </span>
      </div>

      {/* Page Header */}
      <div>
        <h1
          data-testid="company-management-heading"
          className="text-3xl font-bold text-text-dark font-manrope"
        >
          Company Management
        </h1>
      </div>

      {(errorMessage || successMessage) && (
        <div className="space-y-3">
          {errorMessage && (
            <div
              data-testid="company-form-error"
              className="rounded-lg border border-accent-red/40 bg-accent-red/10 px-4 py-3 text-sm text-accent-red font-ibm"
            >
              {errorMessage}
            </div>
          )}
          {successMessage && (
            <div
              data-testid="company-form-success"
              className="rounded-lg border border-accent-green/40 bg-accent-green/10 px-4 py-3 text-sm text-accent-green font-ibm"
            >
              {successMessage}
            </div>
          )}
        </div>
      )}

      {loading && (
        <div
          data-testid="company-form-loading"
          className="rounded-lg border border-accent-blue/30 bg-accent-blue/10 px-4 py-3 text-sm text-accent-blue font-ibm"
        >
          Loading company details...
        </div>
      )}

      {/* Company Basic Information */}
      <Card className="bg-white rounded-xl border border-gray-100 shadow-sm">
        <div className="border-b-4 border-accent-blue px-6 py-4">
          <h2 data-testid="company-info-heading" className="text-lg font-semibold text-text-dark font-manrope">COMPANY INFORMATION</h2>
        </div>
        
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Company Name */}
            <div className="space-y-2">
              <Label className="text-text-dark font-ibm font-medium">
                Company Name<span className="text-accent-red ml-1">*</span>
              </Label>
              <Input
                data-testid="company-name-input"
                placeholder="Enter company name"
                value={companyData.name}
                onChange={(e) => handleCompanyChange('name', e.target.value)}
                className="border-gray-200 focus:border-accent-blue focus:ring-accent-blue font-ibm"
                required
              />
            </div>

            {/* Company Code */}
            <div className="space-y-2">
              <Label className="text-text-dark font-ibm font-medium">
                Company Code<span className="text-accent-red ml-1">*</span>
              </Label>
              <Input
                data-testid="company-code-input"
                placeholder="e.g., ABC001"
                value={companyData.code}
                onChange={(e) => handleCompanyChange('code', e.target.value)}
                disabled={isEditMode}
                className="border-gray-200 focus:border-accent-blue focus:ring-accent-blue font-ibm disabled:cursor-not-allowed disabled:bg-gray-50"
                required
              />
              <p data-testid="company-code-help" className="text-xs text-text-secondary font-ibm">
                {isEditMode ? 'Company code cannot be changed after creation.' : 'Unique identifier for login'}
              </p>
            </div>

            {/* Email */}
            <div className="space-y-2">
              <Label className="text-text-dark font-ibm font-medium">
                Email Address<span className="text-accent-red ml-1">*</span>
              </Label>
              <Input
                data-testid="company-email-input"
                type="email"
                placeholder="contact@company.com"
                value={companyData.email}
                onChange={(e) => handleCompanyChange('email', e.target.value)}
                className="border-gray-200 focus:border-accent-blue focus:ring-accent-blue font-ibm"
                required
              />
            </div>

            {/* Phone */}
            <div className="space-y-2">
              <Label className="text-text-dark font-ibm font-medium">
                Phone Number<span className="text-accent-red ml-1">*</span>
              </Label>
              <Input
                data-testid="company-phone-input"
                placeholder="+91 98765 43210"
                value={companyData.phone}
                onChange={(e) => handleCompanyChange('phone', e.target.value)}
                className="border-gray-200 focus:border-accent-blue focus:ring-accent-blue font-ibm"
                required
              />
            </div>

            {/* Secondary Phone */}
            <div className="space-y-2">
              <Label className="text-text-dark font-ibm font-medium">Secondary Mobile Number</Label>
              <Input
                data-testid="company-secondary-phone-input"
                placeholder="+91 98765 43211"
                value={companyData.secondaryPhone}
                onChange={(e) => handleCompanyChange('secondaryPhone', e.target.value)}
                className="border-gray-200 focus:border-accent-blue focus:ring-accent-blue font-ibm"
              />
            </div>

            {/* GST Number */}
            <div className="space-y-2">
              <Label className="text-text-dark font-ibm font-medium">GST Number</Label>
              <Input
                data-testid="company-gst-input"
                placeholder="27AABCU9603R1ZN"
                value={companyData.gstNumber}
                onChange={(e) => handleCompanyChange('gstNumber', e.target.value)}
                className="border-gray-200 focus:border-accent-blue focus:ring-accent-blue font-ibm"
              />
            </div>

            {/* PAN Number */}
            <div className="space-y-2">
              <Label className="text-text-dark font-ibm font-medium">PAN Number</Label>
              <Input
                data-testid="company-pan-input"
                placeholder="ABCDE1234F"
                value={companyData.panNumber}
                onChange={(e) => handleCompanyChange('panNumber', e.target.value)}
                className="border-gray-200 focus:border-accent-blue focus:ring-accent-blue font-ibm"
              />
            </div>

            {/* CIN Number */}
            <div className="space-y-2">
              <Label className="text-text-dark font-ibm font-medium">CIN Number</Label>
              <Input
                data-testid="company-cin-input"
                placeholder="U12345MH2025PLC123456"
                value={companyData.cinNumber}
                onChange={(e) => handleCompanyChange('cinNumber', e.target.value)}
                className="border-gray-200 focus:border-accent-blue focus:ring-accent-blue font-ibm"
              />
            </div>

            {/* Company Logo Upload */}
            <div className="space-y-3 md:col-span-2">
              <Label className="text-text-dark font-ibm font-medium">Company Logo</Label>
              
              {companyData.logoUrl ? (
                <div className="border border-gray-200 rounded-lg p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <img 
                        src={companyData.logoUrl} 
                        alt="Company Logo" 
                        className="h-16 w-16 object-contain border border-gray-200 rounded"
                        onError={(e) => {
                          e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjQiIGhlaWdodD0iNjQiIHZpZXdCb3g9IjAgMCA2NCA2NCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iNjQiIGhlaWdodD0iNjQiIGZpbGw9IiNGM0Y0RjYiLz48cGF0aCBkPSJNMjQgMjRMMzIgMzJMMjQgNDBNNDAgMjRMMzIgMzJMNDAgNDAiIHN0cm9rZT0iIzlDQTNCNCIgc3Ryb2tlLXdpZHRoPSIyIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiLz48L3N2Zz4=';
                        }}
                      />
                      <div>
                        <p className="text-sm font-medium text-text-dark">Logo uploaded</p>
                        <p className="text-xs text-text-secondary">Will appear on all prints</p>
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleRemoveLogo}
                      className="text-accent-red hover:bg-red-50"
                    >
                      Remove
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="border border-dashed border-gray-300 rounded-lg p-4 space-y-3">
                  <div className="flex items-center gap-3">
                    <Input
                      type="file"
                      accept="image/*"
                      data-testid="company-logo-upload"
                      onChange={(e) => setLogoFile(e.target.files[0])}
                      className="flex-1 border-gray-200 focus:border-accent-blue font-ibm"
                    />
                    <Button
                      type="button"
                      onClick={handleLogoUpload}
                      disabled={uploadingLogo || !logoFile}
                      className="bg-accent-blue hover:bg-accent-blue/90 whitespace-nowrap"
                    >
                      {uploadingLogo ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Uploading...
                        </>
                      ) : (
                        <>
                          <Upload className="w-4 h-4 mr-2" />
                          Upload
                        </>
                      )}
                    </Button>
                  </div>
                  <p className="text-xs text-text-secondary font-ibm">
                    Recommended: PNG or JPG, max 2MB. Logo will be displayed on all dockets, estimates, and prints.
                  </p>
                </div>
              )}
            </div>

            {/* Business Modes */}
            <div className="space-y-2 md:col-span-2">
              <Label className="text-text-dark font-ibm font-medium">Business Modes</Label>
              <div className="flex flex-wrap gap-3">
                {BUSINESS_MODE_OPTIONS.map((mode) => (
                  <label key={mode} className="flex items-center gap-2 text-sm text-text-secondary font-ibm">
                    <input
                      type="checkbox"
                      data-testid={`company-business-mode-${mode.toLowerCase()}`}
                      checked={companyData.businessModes.includes(mode)}
                      onChange={() => toggleCompanyArrayValue('businessModes', mode)}
                      className="h-4 w-4 text-accent-blue border-gray-300 rounded"
                    />
                    {mode.replace('_', ' ')}
                  </label>
                ))}
              </div>
            </div>

            {/* Shipment Types */}
            <div className="space-y-2 md:col-span-2">
              <Label className="text-text-dark font-ibm font-medium">Shipment Types</Label>
              <div className="flex flex-wrap gap-3">
                {SHIPMENT_TYPE_OPTIONS.map((type) => (
                  <label key={type} className="flex items-center gap-2 text-sm text-text-secondary font-ibm">
                    <input
                      type="checkbox"
                      data-testid={`company-shipment-type-${type.toLowerCase()}`}
                      checked={companyData.shipmentTypes.includes(type)}
                      onChange={() => toggleCompanyArrayValue('shipmentTypes', type)}
                      className="h-4 w-4 text-accent-blue border-gray-300 rounded"
                    />
                    {type}
                  </label>
                ))}
              </div>
            </div>

            {/* Default Mode */}
            <div className="space-y-2">
              <Label className="text-text-dark font-ibm font-medium">Default Mode</Label>
              <Select value={companyData.defaultMode} onValueChange={(val) => handleCompanyChange('defaultMode', val)}>
                <SelectTrigger data-testid="company-default-mode-select" className="border-gray-200 focus:border-accent-blue font-ibm">
                  <SelectValue placeholder="Select Default Mode" />
                </SelectTrigger>
                <SelectContent>
                  {BUSINESS_MODE_OPTIONS.map((mode) => (
                    <SelectItem key={mode} data-testid={`company-default-mode-${mode.toLowerCase()}`} value={mode}>
                      {mode.replace('_', ' ')}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Status */}
            <div className="space-y-2">
              <Label className="text-text-dark font-ibm font-medium">Status</Label>
              <Select value={companyData.status} onValueChange={(val) => handleCompanyChange('status', val)}>
                <SelectTrigger data-testid="company-status-select" className="border-gray-200 focus:border-accent-blue font-ibm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem data-testid="company-status-option-active" value="active">Active</SelectItem>
                  <SelectItem data-testid="company-status-option-inactive" value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Address */}
            <div className="space-y-2 md:col-span-2 lg:col-span-3">
              <Label className="text-text-dark font-ibm font-medium">Registered Address</Label>
              <Textarea
                data-testid="company-address-textarea"
                placeholder="Enter full registered address"
                value={companyData.address}
                onChange={(e) => handleCompanyChange('address', e.target.value)}
                className="border-gray-200 focus:border-accent-blue focus:ring-accent-blue font-ibm min-h-[80px]"
              />
            </div>
          </div>
        </div>
      </Card>

      {/* Docket Number Series — Global (informational only) */}
      <Card className="bg-white rounded-xl border border-gray-100 shadow-sm">
        <div className="border-b-4 border-accent-cyan px-6 py-4">
          <div className="flex items-center gap-3">
            <Hash className="w-5 h-5 text-accent-cyan" />
            <h2 data-testid="docket-series-heading" className="text-lg font-semibold text-text-dark font-manrope">
              DOCKET NUMBER SERIES
            </h2>
          </div>
          <p className="text-sm text-text-secondary mt-1 font-ibm">
            Docket numbering is now managed at the platform level
          </p>
        </div>
        <div className="p-6">
          <div className="flex items-start gap-3 bg-blue-50 border border-blue-200 rounded-xl px-4 py-4">
            <svg className="w-5 h-5 text-blue-500 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            <div>
              <p className="text-sm font-semibold text-blue-800 font-ibm">Global Docket Series is Active</p>
              <p className="text-xs text-blue-700 font-ibm mt-1">
                All new dockets across all companies use a single shared counter.
                To configure the prefix, next number, or padding, go to{' '}
                <a href="/settings/docket-series" className="underline font-semibold hover:text-blue-900">
                  Settings → Global Docket Series
                </a>.
              </p>
            </div>
          </div>
        </div>
      </Card>

      {/* Estimate Number Series - Company Level */}
      <Card className="bg-white rounded-xl border border-gray-100 shadow-sm">
        <div className="border-b-4 border-amber-500 px-6 py-4">
          <div className="flex items-center gap-3">
            <Hash className="w-5 h-5 text-amber-500" />
            <h2 data-testid="estimate-series-heading" className="text-lg font-semibold text-text-dark font-manrope">
              ESTIMATE NUMBER SERIES (Company Level)
            </h2>
          </div>
          <p className="text-sm text-text-secondary mt-1 font-ibm">
            Configure estimate / quotation number generation. Format:&nbsp;
            <span className="font-mono">PREFIX-YYYYMM-NNNN</span> — counter resets monthly.
          </p>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Estimate Prefix */}
            <div className="space-y-2">
              <Label className="text-text-dark font-ibm font-medium">
                Estimate Prefix<span className="text-accent-red ml-1">*</span>
              </Label>
              <Input
                data-testid="estimate-prefix-input"
                placeholder="e.g. SLPLEST"
                value={estimateSeries.estimate_prefix}
                onChange={(e) => handleEstimateSeriesChange('estimate_prefix', e.target.value.toUpperCase())}
                className="border-gray-200 focus:border-amber-500 focus:ring-amber-500 font-ibm uppercase"
                maxLength={50}
              />
              <p className="text-xs text-text-secondary font-ibm">
                Required to enable per-company estimate numbering. Leave blank to use the legacy `EST` prefix.
              </p>
            </div>

            {/* Padding Digits */}
            <div className="space-y-2">
              <Label className="text-text-dark font-ibm font-medium">Padding Digits</Label>
              <Input
                data-testid="estimate-padding-input"
                type="number"
                min="2"
                max="8"
                value={estimateSeries.padding_digits}
                onChange={(e) => handleEstimateSeriesChange('padding_digits', parseInt(e.target.value) || 4)}
                className="border-gray-200 focus:border-amber-500 focus:ring-amber-500 font-ibm"
              />
              <p className="text-xs text-text-secondary font-ibm">Pad counter with leading zeros (e.g. 4 → 0001)</p>
            </div>

            {/* Next Number (informational only — counter is derived from DB) */}
            <div className="space-y-2">
              <Label className="text-text-dark font-ibm font-medium">Starting Counter</Label>
              <Input
                data-testid="estimate-next-number-input"
                type="number"
                min="1"
                value={estimateSeries.next_number}
                onChange={(e) => handleEstimateSeriesChange('next_number', parseInt(e.target.value) || 1)}
                className="border-gray-200 focus:border-amber-500 focus:ring-amber-500 font-ibm"
              />
              <p className="text-xs text-text-secondary font-ibm">Initial value; actual counter is auto-managed per month.</p>
            </div>
          </div>

          {/* Preview */}
          {estimateSeries.estimate_prefix && (
            <div className="mt-6 p-4 bg-amber-50 rounded-lg border border-amber-200">
              <p className="text-sm text-text-dark font-ibm">
                <span className="font-semibold">Preview:</span>{' '}
                <span className="font-mono text-amber-700">
                  {estimateSeries.estimate_prefix}-{new Date().toISOString().slice(0,7).replace('-', '')}-{String(estimateSeries.next_number || 1).padStart(estimateSeries.padding_digits || 4, '0')}
                </span>
              </p>
              <p className="text-xs text-text-secondary font-ibm mt-1">
                This format will be used for ALL estimates created under this company. The YYYYMM segment resets the counter every month automatically.
              </p>
            </div>
          )}
        </div>
      </Card>

      {/* Form Schema Mapping Section */}
      {companyData.shipmentTypes.length > 0 && (
        <Card className="bg-white rounded-xl border border-gray-100 shadow-sm">
          <div className="border-b-4 border-violet-500 px-6 py-4">
            <div className="flex items-center gap-3">
              <FileText className="w-5 h-5 text-violet-500" />
              <h2 data-testid="schema-mapping-heading" className="text-lg font-semibold text-text-dark font-manrope">
                FORM SCHEMA MAPPING
              </h2>
            </div>
            <p className="text-sm text-text-secondary mt-1 font-ibm">
              Assign a form schema to each enabled shipment type. This determines which form fields appear when creating a docket.
            </p>
          </div>
          
          <div className="p-6">
            <div className="space-y-4">
              {companyData.shipmentTypes.map((type) => (
                <div key={type} className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="w-32 shrink-0">
                    <span data-testid={`schema-map-type-${type.toLowerCase()}`} className="text-sm font-semibold text-text-dark font-ibm">
                      {type}
                    </span>
                  </div>
                  <div className="flex-1">
                    <Select
                      value={schemaMappings[type] || "none"}
                      onValueChange={(val) => handleSchemaMappingChange(type, val === "none" ? '' : val)}
                    >
                      <SelectTrigger
                        data-testid={`schema-map-select-${type.toLowerCase()}`}
                        className="border-gray-200 focus:border-accent-blue font-ibm bg-white"
                      >
                        <SelectValue placeholder="Select Schema" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">-- No Schema --</SelectItem>
                        {availableSchemas.map((schema) => (
                          <SelectItem key={schema.schema_key} value={schema.schema_key}>
                            {schema.schema_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              ))}
            </div>

            {availableSchemas.length === 0 && (
              <p className="text-sm text-accent-red font-ibm mt-2">
                No form schemas available. Please seed schemas first.
              </p>
            )}
          </div>
        </Card>
      )}

      {/* Branches Section */}
      {branches.map((branch, index) => (
        <Card key={branch.id} className="bg-white rounded-xl border border-gray-100 shadow-sm">
          <div className="border-b-4 border-accent-green px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <MapPin className="w-5 h-5 text-accent-green" />
              <h2 data-testid={`branch-section-title-${branch.id}`} className="text-lg font-semibold text-text-dark font-manrope">
                {branch.isHeadOffice ? 'Head Office (Main Branch)' : `Branch ${index}`}
              </h2>
            </div>
            <div className="flex items-center gap-2">
              {index === branches.length - 1 && (
                <Button
                  data-testid="add-branch-button"
                  onClick={addNewBranch}
                  className="bg-accent-cyan hover:bg-accent-cyan/90 text-white font-ibm font-semibold h-9 px-4 rounded-lg"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Branch
                </Button>
              )}
              {!branch.isHeadOffice && (
                <Button
                  data-testid={`remove-branch-${branch.id}`}
                  onClick={() => removeBranch(branch.id)}
                  variant="outline"
                  className="border-accent-red text-accent-red hover:bg-accent-red hover:text-white font-ibm font-semibold h-9 px-4 rounded-lg"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Remove
                </Button>
              )}
            </div>
          </div>
          
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* Branch Nick Name */}
              <div className="space-y-2">
                <Label className="text-text-dark font-ibm font-medium">
                  Branch Nick Name<span className="text-accent-red ml-1">*</span>
                </Label>
                <Input
                  data-testid={`branch-name-input-${branch.id}`}
                  placeholder="e.g., Mumbai HQ"
                  value={branch.nickName}
                  onChange={(e) => handleBranchChange(branch.id, 'nickName', e.target.value)}
                  className="border-gray-200 focus:border-accent-blue focus:ring-accent-blue font-ibm"
                  required
                />
              </div>

              {/* Address Line 1 */}
              <div className="space-y-2">
                <Label className="text-text-dark font-ibm font-medium">
                  Address Line 1<span className="text-accent-red ml-1">*</span>
                </Label>
                <Input
                  data-testid={`address-line1-input-${branch.id}`}
                  placeholder="Building, Street"
                  value={branch.addressLine1}
                  onChange={(e) => handleBranchChange(branch.id, 'addressLine1', e.target.value)}
                  className="border-gray-200 focus:border-accent-blue focus:ring-accent-blue font-ibm"
                  required
                />
              </div>

              {/* Address Line 2 */}
              <div className="space-y-2">
                <Label className="text-text-dark font-ibm font-medium">Address Line 2</Label>
                <Input
                  data-testid={`address-line2-input-${branch.id}`}
                  placeholder="Area, Landmark"
                  value={branch.addressLine2}
                  onChange={(e) => handleBranchChange(branch.id, 'addressLine2', e.target.value)}
                  className="border-gray-200 focus:border-accent-blue focus:ring-accent-blue font-ibm"
                />
              </div>

              {/* State */}
              <div className="space-y-2">
                <Label className="text-text-dark font-ibm font-medium">
                  State<span className="text-accent-red ml-1">*</span>
                </Label>
                <Select value={branch.state} onValueChange={(val) => handleBranchChange(branch.id, 'state', val)}>
                  <SelectTrigger data-testid={`state-select-${branch.id}`} className="border-gray-200 focus:border-accent-blue font-ibm">
                    <SelectValue placeholder="-Select State-" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="maharashtra">Maharashtra</SelectItem>
                    <SelectItem value="delhi">Delhi</SelectItem>
                    <SelectItem value="gujarat">Gujarat</SelectItem>
                    <SelectItem value="karnataka">Karnataka</SelectItem>
                    <SelectItem value="telangana">Telangana</SelectItem>
                    <SelectItem value="tamilnadu">Tamil Nadu</SelectItem>
                    <SelectItem value="westbengal">West Bengal</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* City */}
              <div className="space-y-2">
                <Label className="text-text-dark font-ibm font-medium">
                  City<span className="text-accent-red ml-1">*</span>
                </Label>
                <Select value={branch.city} onValueChange={(val) => handleBranchChange(branch.id, 'city', val)}>
                  <SelectTrigger data-testid={`city-select-${branch.id}`} className="border-gray-200 focus:border-accent-blue font-ibm">
                    <SelectValue placeholder="-Select City-" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="mumbai">Mumbai</SelectItem>
                    <SelectItem value="delhi">Delhi</SelectItem>
                    <SelectItem value="bangalore">Bangalore</SelectItem>
                    <SelectItem value="hyderabad">Hyderabad</SelectItem>
                    <SelectItem value="ahmedabad">Ahmedabad</SelectItem>
                    <SelectItem value="chennai">Chennai</SelectItem>
                    <SelectItem value="kolkata">Kolkata</SelectItem>
                    <SelectItem value="pune">Pune</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Country */}
              <div className="space-y-2">
                <Label className="text-text-dark font-ibm font-medium">Country</Label>
                <Input
                  data-testid={`country-input-${branch.id}`}
                  placeholder="Country"
                  value={branch.country}
                  onChange={(e) => handleBranchChange(branch.id, 'country', e.target.value)}
                  className="border-gray-200 focus:border-accent-blue focus:ring-accent-blue font-ibm"
                />
              </div>

              {/* Pincode */}
              <div className="space-y-2">
                <Label className="text-text-dark font-ibm font-medium">
                  Pincode<span className="text-accent-red ml-1">*</span>
                </Label>
                <Input
                  data-testid={`pincode-input-${branch.id}`}
                  placeholder="400001"
                  value={branch.pincode}
                  onChange={(e) => handleBranchChange(branch.id, 'pincode', e.target.value)}
                  className="border-gray-200 focus:border-accent-blue focus:ring-accent-blue font-ibm"
                  required
                />
              </div>

              {/* Branch GST Number */}
              <div className="space-y-2">
                <Label className="text-text-dark font-ibm font-medium">Branch GST Number</Label>
                <Input
                  data-testid={`branch-gst-input-${branch.id}`}
                  placeholder="27AABCU9603R1ZN"
                  value={branch.gstNumber}
                  onChange={(e) => handleBranchChange(branch.id, 'gstNumber', e.target.value)}
                  className="border-gray-200 focus:border-accent-blue focus:ring-accent-blue font-ibm"
                />
              </div>

              {/* Contact Person */}
              <div className="space-y-2">
                <Label className="text-text-dark font-ibm font-medium">
                  Contact Person<span className="text-accent-red ml-1">*</span>
                </Label>
                <Input
                  data-testid={`contact-person-input-${branch.id}`}
                  placeholder="Contact person name"
                  value={branch.contactPerson}
                  onChange={(e) => handleBranchChange(branch.id, 'contactPerson', e.target.value)}
                  className="border-gray-200 focus:border-accent-blue focus:ring-accent-blue font-ibm"
                  required
                />
              </div>

              {/* Designation */}
              <div className="space-y-2">
                <Label className="text-text-dark font-ibm font-medium">
                  Designation<span className="text-accent-red ml-1">*</span>
                </Label>
                <Input
                  data-testid={`designation-input-${branch.id}`}
                  placeholder="Branch Manager"
                  value={branch.designation}
                  onChange={(e) => handleBranchChange(branch.id, 'designation', e.target.value)}
                  className="border-gray-200 focus:border-accent-blue focus:ring-accent-blue font-ibm"
                  required
                />
              </div>

              {/* Contact Number */}
              <div className="space-y-2">
                <Label className="text-text-dark font-ibm font-medium">
                  Contact Number<span className="text-accent-red ml-1">*</span>
                </Label>
                <Input
                  data-testid={`contact-number-input-${branch.id}`}
                  placeholder="+91 98765 43210"
                  value={branch.contactNumber}
                  onChange={(e) => handleBranchChange(branch.id, 'contactNumber', e.target.value)}
                  className="border-gray-200 focus:border-accent-blue focus:ring-accent-blue font-ibm"
                  required
                />
              </div>

              {/* Branch Email */}
              <div className="space-y-2">
                <Label className="text-text-dark font-ibm font-medium">Branch Email</Label>
                <Input
                  data-testid={`branch-email-input-${branch.id}`}
                  type="email"
                  placeholder="branch@company.com"
                  value={branch.email}
                  onChange={(e) => handleBranchChange(branch.id, 'email', e.target.value)}
                  className="border-gray-200 focus:border-accent-blue focus:ring-accent-blue font-ibm"
                />
              </div>
            </div>
          </div>
        </Card>
      ))}

      {/* Action Buttons */}
      <div className="flex items-center justify-end gap-4 pb-8">
        <Button
          data-testid="cancel-button"
          variant="outline"
          onClick={() => navigate('/settings/companies')}
          disabled={saving}
          className="bg-accent-orange hover:bg-accent-orange/90 text-white border-0 font-ibm font-semibold h-11 px-8 rounded-lg disabled:opacity-70"
        >
          Cancel
        </Button>
        <Button
          data-testid="save-company-button"
          onClick={handleSave}
          disabled={saving || loading}
          className="bg-accent-green hover:bg-accent-green/90 text-white font-ibm font-semibold h-11 px-8 rounded-lg disabled:opacity-70"
        >
          {saving ? 'Saving...' : isEditMode ? 'Update Company' : 'Save Company'}
        </Button>
      </div>
    </div>
  );
};

export default AddCompany;
